import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { AI_ENDPOINTS } from 'features/ai-generators/constants';
import { useShowAlert } from 'hooks';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { FPActionButton, TextBox, When } from 'UI/components/atoms';
import { getErrorMessage } from 'UI/utils';

import { SaveButtonStyled } from '../styles';

const COPIES = strings.ai.generators.roleplay.feedbackDrawer.tabs.aiInsights;

export const ReportIssue = ({ callId, issueComment, issueCommentDate, onRefreshData }) => {
  const { showAlert } = useShowAlert();
  const [isReportingIssue, setIsReportingIssue] = useState(false);
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
  const [issueText, setIssueText] = useState('');

  const handleEnableReportIssue = () => {
    setIsReportingIssue(true);
  };
  const handleCancelReportIssue = () => {
    setIsReportingIssue(false);
    setIssueText('');
  };

  const handleTextChange = (_name, text) => {
    setIssueText(text);
  };

  const handleReportIssue = async () => {
    if (!issueText.trim().length) return;

    try {
      setIsSubmittingIssue(true);
      const body = { issue: issueText };
      const response = await SECONDARY_API_CLIENT.patch(
        AI_ENDPOINTS.roleplayReportIssue.replace(':id', callId),
        body
      );

      if (
        [HTTPStatusCodes.Ok, HTTPStatusCodes.Created, HTTPStatusCodes.NoContent].includes(
          response.status
        )
      ) {
        setIsReportingIssue(false);
        onRefreshData && onRefreshData();
        setIssueText('');
      }
    } catch (error) {
      showAlert({
        body: getErrorMessage(error, COPIES.alert.message),
        title: COPIES.alert.title,
        severity: 'error'
      });
    } finally {
      setIsSubmittingIssue(false);
    }
  };

  return (
    <>
      <When condition={issueComment}>
        <Box alignItems="center" display="flex" flexDirection="row" justifyContent="space-between">
          <Box display="flex" flexDirection="row" gridGap={8}>
            <FlagOutlinedIcon color="primary" />
            <Typography variant="subtitle1">{COPIES.issueReported}</Typography>
          </Box>
          <Typography color="textSecondary" variant="caption">
            {issueCommentDate}
          </Typography>
        </Box>
        <Typography variant="body2">{issueComment}</Typography>
      </When>

      <When condition={!issueComment}>
        <Box alignItems="center" display="flex" flexDirection="column" gridGap={16}>
          <Typography variant="subtitle1">{COPIES.somethingWrong}</Typography>
          <When condition={!isReportingIssue}>
            <Button
              color="primary"
              onClick={handleEnableReportIssue}
              startIcon={<FlagOutlinedIcon />}
            >
              {COPIES.reportAnIssue}
            </Button>
          </When>

          <When condition={isReportingIssue}>
            <TextBox
              autoFocus
              fullWidth
              multiline
              name="issue"
              noMargin
              onChange={handleTextChange}
              placeholder={COPIES.describeIssueLabel}
              value={issueText}
            />

            <Box
              display="flex"
              flexDirection="row"
              gridGap={16}
              justifyContent="flex-end"
              width="100%"
            >
              <FPActionButton
                disabled={isSubmittingIssue}
                onClick={handleCancelReportIssue}
                text={strings.shared.ui.cancel}
                variant="outlined"
              />

              <SaveButtonStyled
                isSaving={isSubmittingIssue}
                onClick={handleReportIssue}
                startIcon={
                  isSubmittingIssue ? <CircularProgress color="inherit" size={20} /> : null
                }
                text={strings.shared.ui.submit}
              />
            </Box>
          </When>
        </Box>
      </When>
    </>
  );
};
